import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';
import { ImageCacheSummary } from '../../hooks/useZoomPreview';
import {
  MASTER_LABEL_IMAGE_SLIDER_MAX,
  MASTER_LABEL_IMAGE_SLIDER_MIN,
} from '../../utils/masterLabelImageSizing';

export const MASTER_LABEL_TEXT_SCALE_MIN = 50;
export const MASTER_LABEL_TEXT_SCALE_MAX = 200;

interface MasterLabelWorkspaceControlsProps {
  textScale: number;
  onTextScaleChange: (value: number) => void;
  cropSize: number;
  onCropSizeChange: (value: number) => void;
  imageCacheSummary: ImageCacheSummary;
  isLoadingRemainingImages: boolean;
  onLoadRemainingImages: () => void;
  autoReloadImages: boolean;
  onToggleAutoReloadImages: () => void;
  zoomEnabled: boolean;
  onToggleZoom: () => void;
  toolbarButtonPaddingY?: number;
  style?: React.CSSProperties;
}

export const MasterLabelWorkspaceControls: React.FC<MasterLabelWorkspaceControlsProps> = ({
  textScale,
  onTextScaleChange,
  cropSize,
  onCropSizeChange,
  imageCacheSummary,
  isLoadingRemainingImages,
  onLoadRemainingImages,
  autoReloadImages,
  onToggleAutoReloadImages,
  zoomEnabled,
  onToggleZoom,
  toolbarButtonPaddingY = 3,
  style,
}) => {
  const loadRemainingDisabled =
    isLoadingRemainingImages ||
    imageCacheSummary.total === 0 ||
    (imageCacheSummary.failed === 0 && imageCacheSummary.idle === 0);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexShrink: 0,
        ...style,
      }}
    >
      <span
        style={{
          fontSize: 9,
          color: 'var(--text-tertiary)',
          fontFamily: 'var(--font-mono)',
          whiteSpace: 'nowrap',
        }}
      >
        {textScale}%
      </span>
      <input
        aria-label="Master Label text scale"
        type="range"
        min={MASTER_LABEL_TEXT_SCALE_MIN}
        max={MASTER_LABEL_TEXT_SCALE_MAX}
        value={textScale}
        onChange={(event) => onTextScaleChange(Number(event.target.value))}
        style={{ width: 80, accentColor: 'var(--accent)' }}
      />
      <span
        style={{
          fontSize: 9,
          color: 'var(--text-tertiary)',
          fontFamily: 'var(--font-mono)',
          whiteSpace: 'nowrap',
        }}
      >
        {cropSize}px
      </span>
      <input
        aria-label="Master Label crop size"
        type="range"
        min={MASTER_LABEL_IMAGE_SLIDER_MIN}
        max={MASTER_LABEL_IMAGE_SLIDER_MAX}
        value={cropSize}
        onChange={(event) => onCropSizeChange(Number(event.target.value))}
        style={{ width: 80, accentColor: 'var(--accent)' }}
      />
      <button
        type="button"
        onClick={onLoadRemainingImages}
        disabled={loadRemainingDisabled}
        aria-label="Retry any crop images that are still not loaded"
        style={{
          fontSize: 9,
          fontFamily: 'var(--font-mono)',
          fontWeight: 600,
          padding: `${toolbarButtonPaddingY}px 6px`,
          borderRadius: 'var(--radius-sm)',
          cursor: loadRemainingDisabled ? 'not-allowed' : 'pointer',
          border: '1px solid var(--border)',
          background: 'var(--bg)',
          color: 'var(--text-primary)',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          transition: 'all 150ms',
          opacity: loadRemainingDisabled ? 0.45 : 1,
        }}
      >
        {isLoadingRemainingImages ? (
          <CircularProgress size={11} style={{ color: 'var(--text-tertiary)' }} />
        ) : (
          <CloudDownloadOutlinedIcon style={{ fontSize: 11 }} />
        )}
        Load Remaining
      </button>
      <span
        style={{
          fontSize: 9,
          color: imageCacheSummary.failed > 0 ? 'var(--danger)' : 'var(--text-tertiary)',
          fontFamily: 'var(--font-mono)',
          whiteSpace: 'nowrap',
        }}
      >
        {imageCacheSummary.loaded}/{imageCacheSummary.total}
      </span>
      <button
        type="button"
        onClick={onToggleAutoReloadImages}
        aria-label={
          autoReloadImages
            ? 'Auto-reload missing crop images (ON)'
            : 'Auto-reload missing crop images (OFF)'
        }
        style={{
          fontSize: 9,
          fontFamily: 'var(--font-mono)',
          fontWeight: 600,
          padding: `${toolbarButtonPaddingY}px 6px`,
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          border: autoReloadImages ? '1px solid var(--accent-border)' : '1px solid var(--border)',
          background: autoReloadImages ? 'var(--accent-subtle)' : 'var(--bg)',
          color: autoReloadImages ? 'var(--accent)' : 'var(--text-tertiary)',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          transition: 'all 150ms',
        }}
      >
        <AutorenewOutlinedIcon style={{ fontSize: 11 }} /> Auto
      </button>
      <div style={{ width: 1, height: 14, background: 'var(--border)', flexShrink: 0 }} />
      <button
        type="button"
        onClick={onToggleZoom}
        aria-label={zoomEnabled ? 'Zoom on hover (ON)' : 'Zoom on hover (OFF - hold Alt)'}
        style={{
          fontSize: 9,
          fontFamily: 'var(--font-mono)',
          fontWeight: 600,
          padding: `${toolbarButtonPaddingY}px 6px`,
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          border: zoomEnabled ? '1px solid var(--purple-border)' : '1px solid var(--border)',
          background: zoomEnabled ? 'var(--purple-subtle)' : 'var(--bg)',
          color: zoomEnabled ? 'var(--purple)' : 'var(--text-tertiary)',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          transition: 'all 150ms',
        }}
      >
        <ZoomInIcon style={{ fontSize: 11 }} /> Zoom
      </button>
    </div>
  );
};
